/**
 * OccupationalExam Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React from 'react';
import { InlineFilterBar, InlineMultiSelect, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  OccupationalExamStatus,
  ExamTypeLabels,
  WorkTypeLabels,
  ReportTypeLabels,
} from '../../../types/occupationalExam';

// Types
export interface ExamFiltersData {
  searchTerm: string;
  status: string;
  examTypes: string[];
  workTypes: string[];
  reportTypes: string[];
}

interface Props {
  onFilter: (filters: ExamFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const STATUS_OPTIONS = [
  { value: OccupationalExamStatus.ACTIVE, label: 'Ativo' },
  { value: OccupationalExamStatus.INACTIVE, label: 'Inativo' },
];

const EXAM_TYPE_OPTIONS = Object.entries(ExamTypeLabels).map(([value, label]) => ({
  value,
  label,
}));
const WORK_TYPE_OPTIONS = Object.entries(WorkTypeLabels).map(([value, label]) => ({
  value,
  label,
}));
const REPORT_TYPE_OPTIONS = Object.entries(ReportTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const defaultValues: ExamFiltersData = {
  searchTerm: '',
  status: '',
  examTypes: [],
  workTypes: [],
  reportTypes: [],
};

export const OccupationalExamFiltersComponent: React.FC<Props> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      examTypes: 'Tipos',
      workTypes: 'Aplicabilidade',
      reportTypes: 'Relatórios',
    },
    formatValue: {
      status: (v: string) => STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v,
      examTypes: (v: string[]) => v.map((x) => ExamTypeLabels[x] ?? x).join(', '),
      workTypes: (v: string[]) => v.map((x) => WorkTypeLabels[x] ?? x).join(', '),
      reportTypes: (v: string[]) => v.map((x) => ReportTypeLabels[x] ?? x).join(', '),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, código TUSS ou eSocial..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="exames"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={130}
      />
      <InlineMultiSelect
        label="Tipos"
        value={filters.examTypes}
        options={EXAM_TYPE_OPTIONS}
        onChange={(v) => setFilter('examTypes', v)}
        minWidth={150}
      />
      <InlineMultiSelect
        label="Aplicabilidade"
        value={filters.workTypes}
        options={WORK_TYPE_OPTIONS}
        onChange={(v) => setFilter('workTypes', v)}
        minWidth={170}
      />
      <InlineMultiSelect
        label="Relatórios"
        value={filters.reportTypes}
        options={REPORT_TYPE_OPTIONS}
        onChange={(v) => setFilter('reportTypes', v)}
        minWidth={150}
      />
    </InlineFilterBar>
  );
};

export default OccupationalExamFiltersComponent;
